<template>
  <DashboardWindow>
    <template v-slot:title>
      {{ $t('associateAsCustomer') }}
    </template>

    <template v-slot:body>
      <WCSpinner v-if="loading" size="xl" position="center" />
      <h2 class="text-center my-3" aria-level="1" v-else-if="!customers.length">
        {{ $t('noCustomerAvailable') }}
      </h2>
      <DashboardWindowSection class="mx-n3 mx-md-0" v-else>
        <div class="row align-items-end mb-4">
          <div class="col-7 col-sm-9">
            <!-- START:search box -->
            <WCSearchInput
              class="search-input-group"
              :ariaLabel="$t('searchCustomers')"
              :placeHolderText="$t('searchCustomers')"
              @focusIn="hasFocus = true"
              @focusOut="hasFocus = false"
              :hasFocusInput="hasFocus"
              :updatedSearchInput="searchInput"
              @onSearch="updateSearchValue"
              @onKeyDown="updateSearchValue"
              @onClear="updateSearchValue"
              @updateValue="submitSearch(searchInput)"
            />
            <!-- END:search box -->
          </div>
          <div class="col-5 d-flex align-items-center col-sm-3">
            <label for="search-show">{{ $t('show') }}: </label>
            <WCSelect class="ml-2" v-model="request.ps" @input="psChanged">
              <option v-for="(opt, oi) in size_options" :key="oi" :value="opt">{{ opt }}</option>
            </WCSelect>
          </div>
        </div>

        <WCCustomerTable :customers="customers" :customer="isCustomer" :user="user" />

        <div class="col-md-12">
          <div class="d-flex flex-column align-items-center">
            <span class="mb-2">
              {{ $t('showing') }}
              {{ startRecord }} {{ $t('to') }} {{ endRecord }} {{ $t('of') }} {{ totalCount }} ({{
                pagesAvailable
              }}
              {{ $t('pages') }})
            </span>
            <WCPagination
              class="d-none d-sm-block"
              :collectionSize="totalCount"
              v-model="request.pn"
              :pageSize="Number(request.ps)"
              :maxSize="7"
              :ellipsis="false"
              :rotate="false"
              @input="pnChanged"
            />
            <WCPagination
              class="d-sm-none"
              :collectionSize="totalCount"
              v-model="request.pn"
              :pageSize="Number(request.ps)"
              :maxSize="2"
              :ellipsis="false"
              :rotate="false"
              @input="pnChanged"
            />
          </div>
        </div>
      </DashboardWindowSection>
    </template>
  </DashboardWindow>
</template>
<script>
import WCPagination from '@/components/WCPagination/WCPagination.vue';
import WCSelect from '@/modules/forms/components/WCSelect/WCSelect.vue';
import axios from 'axios';
import { mapGetters } from 'vuex';
import WCCustomerTable from '@/components/WCCustomerTable/WCCustomerTable.vue';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCSearchInput from '@/components/WCSearchInput/WCSearchInput.vue';

export default {
  name: 'EmployeeCustomerSelect',
  components: {
    DashboardWindow,
    DashboardWindowSection,
    WCSpinner,
    WCPagination,
    WCSelect,
    WCSearchInput,
    WCCustomerTable,
  },
  data() {
    return {
      loading: false,
      request: {},
      ps: 8,
      pn: 1,
      totalCount: 0,
      size_options: [8, 16, 32, 64], // sorted size options in ascending order
      customers: [],
      searchInput: '',
      cq: '',
      hasFocus: false,
    };
  },
  created() {
    this.search();
  },
  watch: {
    $route: 'search', // Anytime $router is used, the search method is called
  },
  methods: {
    /**
     * updates the search input value on search
     */
    updateSearchValue(searchValue) {
      this.searchInput = searchValue;
      this.submitSearch(this.searchInput);
    },
    async search() {
      const { pn = 1, ps = 16, s = '', cq = '' } = this.$route.query;
      this.loading = true;
      try {
        const response = await axios.get(`/api/employee/cs`, {
          params: {
            ...this.$route.query,
            pn,
            ps,
            s,
            cq,
          },
        });

        this.customers = response.data.items;
        this.request = response.data.request;
        this.totalCount = response.data.totalCount;

        this.searchInput = this.request.cq;
      } catch (error) {
        console.log(error);
      }
      this.loading = false;

      this.pn = Number(pn);
      this.ps = Number(ps);
      this.s = s;
      this.cq = cq;
    },
    pnChanged(pageNumber) {
      this.$router.push({
        name: 'EmployeeCustomerSelect',
        query: { ...this.$route.query, pn: pageNumber === 1 ? 1 : pageNumber },
      });
    },
    psChanged(pageSize) {
      this.$router.push({
        name: 'EmployeeCustomerSelect',
        query: { ...this.$route.query, ps: pageSize === '16' ? 16 : pageSize },
      });
    },
    submitSearch(cq) {
      this.$router.push({
        name: 'EmployeeCustomerSelect',
        query: { ...this.$route.query, cq: cq === '' ? undefined : cq, pn: undefined },
      });
    },
  },
  computed: {
    ...mapGetters({
      isCustomer: 'user/isCustomer',
      user: 'user/getUser',
    }),
    startRecord() {
      if (this.request.pn === 1) {
        return 1;
      }
      return (this.request.pn - 1) * this.ps + 1;
    },
    endRecord() {
      return Math.min((this.totalCount, this.request.pn * this.request.ps));
    },
    pagesAvailable() {
      return Math.ceil(this.totalCount / this.request.ps);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.search-input-group {
  width: 100%;
  .form-control:focus {
    box-shadow: none;
  }
}
@include media-breakpoint-up(sm) {
  .search-input-group {
    width: 50%;
  }
}
</style>
